import { useState, useEffect } from "react";
import { toast } from "sonner";
import { CreditCard, Mail, Sparkles, Save, Loader2, PlugZap, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

const inputCls = "w-full bg-[#09090b] border border-[#27272a] focus:border-[#c8aa6e] outline-none px-3 py-2.5 text-sm text-[#fafafa] placeholder:text-[#52525b] font-mono";

function Field({ id, label, status, value, onChange, placeholder, secret = true }) {
  const configured = status?.configured;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="label-tiny">{label}</label>
        <span className={`text-[10px] flex items-center gap-1 ${configured ? "text-[#22c55e]" : "text-[#71717a]"}`} data-testid={`${id}-status`}>
          {configured ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
          {configured ? (secret ? `Configurado · ${status.masked}` : status.masked) : "Não configurado"}
        </span>
      </div>
      <input data-testid={`${id}-input`} type={secret ? "password" : "text"} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={configured ? "Deixar vazio para manter" : placeholder} className={inputCls} autoComplete="off" />
    </div>
  );
}

function Section({ icon: Icon, title, description, children, onSave, onTest, saving, testing, testid }) {
  return (
    <div className="bg-[#18181b] border border-[#27272a]" data-testid={testid}>
      <div className="px-5 py-4 border-b border-[#27272a] flex items-start gap-3">
        <Icon className="w-4 h-4 text-[#c8aa6e] mt-0.5" strokeWidth={1.5} />
        <div>
          <div className="text-sm text-[#fafafa]">{title}</div>
          <div className="text-xs text-[#71717a] mt-0.5">{description}</div>
        </div>
      </div>
      <div className="p-5 space-y-4">{children}</div>
      <div className="px-5 py-3 border-t border-[#27272a] flex items-center gap-3">
        <button onClick={onSave} disabled={saving} data-testid={`${testid}-save`}
          className="bg-[#c8aa6e] text-black hover:bg-[#d4b982] px-4 py-2 text-sm font-medium flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
        </button>
        <button onClick={onTest} disabled={testing} data-testid={`${testid}-test`}
          className="border border-[#27272a] text-[#fafafa] hover:border-[#c8aa6e] px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlugZap className="w-4 h-4" />} Testar ligação
        </button>
      </div>
    </div>
  );
}

export default function SettingsView() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState("");
  const [testing, setTesting] = useState("");
  const [aiModels, setAiModels] = useState([]);
  const [modelInfo, setModelInfo] = useState(null);
  const [customModel, setCustomModel] = useState(false);

  const load = () => api.get("/admin/settings").then(({ data }) => setStatus(data));
  const loadModels = () =>
    api.get("/admin/settings/ai-models")
      .then(({ data }) => { setAiModels(data.models || []); setModelInfo(data); })
      .catch(() => { setAiModels([]); });
  useEffect(() => { load(); loadModels(); }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (keys, section) => {
    const body = {};
    keys.forEach((k) => { if (form[k]?.trim()) body[k] = form[k].trim(); });
    if (!Object.keys(body).length) { toast.info("Nada para guardar"); return; }
    setSaving(section);
    try {
      const { data } = await api.put("/admin/settings", body);
      setStatus(data);
      setForm((f) => { const n = { ...f }; keys.forEach((k) => delete n[k]); return n; });
      if (section === "ai") { setCustomModel(false); loadModels(); }
      toast.success("Definições guardadas");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally { setSaving(""); }
  };

  const test = async (endpoint, section, body) => {
    setTesting(section);
    try {
      const { data } = await api.post(`/admin/settings/${endpoint}`, body);
      (data.status === "success" ? toast.success : toast.error)(data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no teste");
    } finally { setTesting(""); }
  };

  if (!status) return <div className="text-[#71717a] text-sm">A carregar…</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-settings">
      <p className="text-xs text-[#71717a]">
        As chaves ficam guardadas na base de dados (nunca são devolvidas por completo). Alterações aplicam-se de imediato, sem reiniciar.
      </p>

      <Section icon={Sparkles} title="Inteligência Artificial (OpenAI)" testid="settings-ai"
        description="Necessária para a Pesquisa IA. Obtém a chave em platform.openai.com/api-keys."
        saving={saving === "ai"} testing={testing === "ai"}
        onSave={() => save(["openai_api_key", "openai_model"], "ai")}
        onTest={() => test("test-ai", "ai")}>
        <Field id="openai-key" label="OpenAI API Key" status={status.openai_api_key} value={form.openai_api_key || ""} onChange={set("openai_api_key")} placeholder="sk-..." />
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="label-tiny">Modelo</label>
            <span className="text-[10px] text-[#71717a]" data-testid="openai-model-source">
              {modelInfo?.source === "openai" ? "Modelos da tua conta OpenAI" : "Lista padrão — guarda a chave para ver todos"}
            </span>
          </div>
          {!customModel ? (
            <select data-testid="openai-model-select"
              value={form.openai_model ?? (modelInfo?.current || "")}
              onChange={(e) => {
                if (e.target.value === "__custom__") { setCustomModel(true); set("openai_model")(""); }
                else set("openai_model")(e.target.value);
              }}
              className={inputCls}>
              {aiModels.map((m) => <option key={m} value={m}>{m}</option>)}
              <option value="__custom__">Personalizado…</option>
            </select>
          ) : (
            <div className="flex gap-2">
              <input data-testid="openai-model-input" type="text" value={form.openai_model || ""}
                onChange={(e) => set("openai_model")(e.target.value)}
                placeholder="ex.: gpt-4.1-mini" className={inputCls} autoComplete="off" />
              <button type="button" data-testid="openai-model-list-btn"
                onClick={() => { setCustomModel(false); set("openai_model")(modelInfo?.current || ""); }}
                className="border border-[#27272a] text-[#fafafa] hover:border-[#c8aa6e] px-3 text-xs whitespace-nowrap">
                Ver lista
              </button>
            </div>
          )}
          <p className="text-[10px] text-[#71717a] mt-1">
            Modelo atual: <span className="text-[#c8aa6e]" data-testid="openai-model-current">{modelInfo?.current || "—"}</span>
          </p>
        </div>
      </Section>

      <Section icon={CreditCard} title="Pagamentos (Stripe)" testid="settings-stripe"
        description="Subscrições mensais. Webhook: <domínio>/api/webhook/stripe (eventos checkout.session.completed, invoice.paid, customer.subscription.updated/deleted)."
        saving={saving === "stripe"} testing={testing === "stripe"}
        onSave={() => save(["stripe_secret_key", "stripe_webhook_secret"], "stripe")}
        onTest={() => test("test-stripe", "stripe")}>
        <Field id="stripe-key" label="Secret Key" status={status.stripe_secret_key} value={form.stripe_secret_key || ""} onChange={set("stripe_secret_key")} placeholder="sk_live_... ou sk_test_..." />
        <Field id="stripe-webhook" label="Webhook Signing Secret" status={status.stripe_webhook_secret} value={form.stripe_webhook_secret || ""} onChange={set("stripe_webhook_secret")} placeholder="whsec_..." />
      </Section>

      <Section icon={Mail} title="Email (Resend)" testid="settings-resend"
        description="Envio de emails transacionais e relatórios. O remetente deve pertencer a um domínio verificado no Resend."
        saving={saving === "resend"} testing={testing === "resend"}
        onSave={() => save(["resend_api_key", "resend_from_email"], "resend")}
        onTest={() => {
          const to = window.prompt("Enviar email de teste para:");
          if (to) test("test-email", "resend", { to });
        }}>
        <Field id="resend-key" label="API Key" status={status.resend_api_key} value={form.resend_api_key || ""} onChange={set("resend_api_key")} placeholder="re_..." />
        <Field id="resend-from" label="Email remetente" status={status.resend_from_email} value={form.resend_from_email || ""} onChange={set("resend_from_email")} placeholder="radarx@oteudominio.pt" secret={false} />
      </Section>

      <Section icon={ShieldCheck} title="Anti-bot (ScraperAPI)" testid="settings-scraper"
        description="Necessária para recolher os sites protegidos por Cloudflare (Mulheres VIP e Portuas). Obtém a chave em dashboard.scraperapi.com. Cada página recolhida consome créditos (render)."
        saving={saving === "scraper"} testing={testing === "scraper"}
        onSave={() => save(["scraperapi_key", "scraperapi_mode"], "scraper")}
        onTest={() => test("test-scraper", "scraper")}>
        <Field id="scraper-key" label="ScraperAPI API Key" status={status.scraperapi_key} value={form.scraperapi_key || ""} onChange={set("scraperapi_key")} placeholder="a chave de 32 caracteres do teu painel ScraperAPI" />
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="label-tiny">Modo de bypass Cloudflare</label>
            <span className="text-[10px] text-[#71717a]">Atual: <span className="text-[#c8aa6e]" data-testid="scraper-mode-current">{status.scraperapi_mode?.masked || "ultra_premium"}</span></span>
          </div>
          <select data-testid="scraper-mode-select"
            value={form.scraperapi_mode ?? (status.scraperapi_mode?.masked || "ultra_premium")}
            onChange={(e) => set("scraperapi_mode")(e.target.value)}
            className={inputCls}>
            <option value="ultra_premium">ultra_premium — para Cloudflare managed challenge (~75 créditos/página c/ render)</option>
            <option value="premium">premium — mais barato, IPs residenciais (~25 créditos c/ render)</option>
            <option value="basic">basic — só render=true (~10 créditos; normalmente NÃO passa Cloudflare)</option>
          </select>
          <p className="text-[10px] text-[#71717a] mt-1">Os sites Cloudflare (Mulheres VIP, Portuas) costumam exigir <span className="text-[#c8aa6e]">ultra_premium</span>. Se bloquear, o estado da recolha mostra o erro exato do ScraperAPI.</p>
        </div>
      </Section>
    </div>
  );
}
